/**
 * Öffentliches Alarm-Dashboard – ohne Login.
 * Route: /public · Vollbild 1920×1080, ohne Scrollbars.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Map, Marker } from 'pigeon-maps';
import moment from 'moment-timezone';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { Icon, IconWell } from '../icons/AppIcons';
import Logo from '../images/logo.png';
import '../css/public.css';

const PORT_ICONS = { fire: 'fire', door: 'door', none: 'emptyPort' };
const PORT_LABELS = { fire: 'Feuermelder', door: 'Türkontakt', none: 'Frei' };

function lampClass(ok) {
  if (ok === true) return 'is-ok';
  if (ok === false) return 'is-bad';
  return 'is-off';
}

function portsOf(item) {
  return [1, 2, 3, 4].map((n) => {
    const type = item[`port_${n}`] || 'none';
    return {
      n,
      type,
      live: Boolean(item[`alarm_${n}`]),
      icon: PORT_ICONS[type] || 'emptyPort',
      label: PORT_LABELS[type] || 'Anschluss',
    };
  });
}

function loadPublic() {
  const url = `${getApiBaseUrl()}v2/public/`;
  return fetch(url, { credentials: 'omit', headers: { Accept: 'application/json' } })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text().then((text) => (text ? JSON.parse(text) : null));
    });
}

export default function PublicAlarms() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [clock, setClock] = useState(() => new Date());

  useEffect(() => {
    document.documentElement.setAttribute('data-public-page', '1');
    return () => document.documentElement.removeAttribute('data-public-page');
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      loadPublic()
        .then((payload) => {
          if (cancelled) return;
          setData(payload);
          setError('');
        })
        .catch(() => {
          if (!cancelled) setError('Status nicht erreichbar');
        });
    };
    tick();
    const poll = setInterval(tick, 4000);
    const time = setInterval(() => setClock(new Date()), 1000);
    return () => {
      cancelled = true;
      clearInterval(poll);
      clearInterval(time);
    };
  }, []);

  const alarms = useMemo(
    () => (Array.isArray(data?.alarms) ? data.alarms : []),
    [data?.alarms]
  );
  const devices = useMemo(
    () => (Array.isArray(data?.devices) ? data.devices : []),
    [data?.devices]
  );
  const alarmOn = alarms.length > 0;
  const online = data?.devices_online ?? 0;
  const total = data?.devices_total ?? 0;
  const offline = Math.max(0, total - online);

  const alarmByDevice = useMemo(() => {
    const map = {};
    alarms.forEach((alarm) => {
      if (map[alarm.device_id]) return;
      map[alarm.device_id] = alarm;
    });
    return map;
  }, [alarms]);

  useEffect(() => {
    document.documentElement.setAttribute('data-public-alarm', alarmOn ? '1' : '0');
    const base = 'FireCore · Alarm-Monitor';
    document.title = alarmOn ? `ALARM · ${alarms.length} offen · ${base}` : base;
    return () => {
      document.documentElement.removeAttribute('data-public-alarm');
      document.title = 'FireCore';
    };
  }, [alarmOn, alarms.length]);

  const locations = useMemo(
    () =>
      devices
        .filter((d) => d.lat && d.lng)
        .map((d) => ({
          ...d,
          latLng: [Number(d.lat), Number(d.lng)],
        })),
    [devices]
  );

  const mapCenter = locations[0]?.latLng || [46.495349, 9.839747];
  const darkTiles = (x, y, z, dpr) =>
    `https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/${z}/${x}/${y}${dpr >= 2 ? '@2x' : ''}.png`;

  const kpis = [
    { value: total, label: 'Koffer', tone: '' },
    { value: online, label: 'Online', tone: 'is-ok' },
    { value: offline, label: 'Offline', tone: '' },
    { value: data?.mqtt_broker ? 'OK' : 'Störung', label: 'Broker', tone: lampClass(data?.mqtt_broker) },
    { value: alarms.length, label: 'Alarme', tone: alarmOn ? 'is-bad' : 'is-ok' },
  ];

  return (
    <div className={`pub ${alarmOn ? 'is-alarm' : 'is-ok'}${locations.length ? ' has-map' : ''}`}>
      <header className="pub-top">
        <div className="pub-brand">
          <img src={Logo} alt="FireCore" />
          <strong>Alarm-Monitor</strong>
        </div>
        <div className="pub-kpis">
          {kpis.map((kpi) => (
            <article key={kpi.label}>
              <strong className={kpi.tone}>{kpi.value}</strong>
              <span>{kpi.label}</span>
            </article>
          ))}
        </div>
        <div className="pub-live">
          <em className={error ? 'is-bad' : 'is-ok'} />
          {error || 'Live'}
        </div>
        <time className="pub-clock">{moment(clock).format('DD.MM.YYYY  HH:mm:ss')}</time>
      </header>

      <section className="pub-hero">
        <IconWell name={alarmOn ? 'alarm' : 'ok'} tone={alarmOn ? 'bad' : 'ok'} size={36} />
        <h1>{alarmOn ? 'Feueralarm' : 'Alles ruhig'}</h1>
        <p>
          {alarmOn
            ? `${alarms.length} offen · ${alarms.map((a) => a.device_name).filter(Boolean).join(', ')}`
            : 'Kein offener Alarm'}
        </p>
      </section>

      <section className="pub-main">
        <div className="pub-koffers">
          {devices.length === 0 && (
            <div className="pub-empty">
              <Icon name="koffer" size={40} />
              <span>Keine Koffer</span>
            </div>
          )}
          {devices.map((device) => {
            const alarm = alarmByDevice[device.id];
            const isAlarm = Boolean(alarm) || device.alarm;
            return (
              <article
                key={device.id}
                className={`pub-koffer ${isAlarm ? 'is-alarm' : device.online ? 'is-online' : 'is-offline'}`}
              >
                <header>
                  <IconWell
                    name={isAlarm ? 'alarm' : device.online ? 'online' : 'offline'}
                    tone={isAlarm ? 'bad' : device.online ? 'ok' : 'mute'}
                    size={32}
                  />
                  <div>
                    <strong>{device.name || `Koffer ${device.id}`}</strong>
                    <span>
                      {isAlarm
                        ? `${alarm?.type || 'ALARM'} · ${alarm?.created ? moment(alarm.created).format('HH:mm:ss') : 'aktiv'}`
                        : device.online
                          ? `Online · ${device.last_seen ? moment(device.last_seen).format('HH:mm') : '–'}`
                          : 'Offline'}
                    </span>
                  </div>
                  <em>{isAlarm ? 'Alarm' : device.online ? 'OK' : 'Offline'}</em>
                </header>
                <div className="pub-ports">
                  {portsOf(alarm || device).map((p) => (
                    <div key={p.n} className={`pub-port ${p.live ? 'is-on' : 'is-off'}`}>
                      <Icon name={p.icon} size={26} />
                      <em>{p.label} {p.n}</em>
                      <b>{p.type === 'none' ? 'Frei' : p.live ? 'Alarm' : 'Ruhe'}</b>
                    </div>
                  ))}
                </div>
              </article>
            );
          })}
        </div>

        {locations.length > 0 && (
          <div className="pub-map">
            <Map
              defaultCenter={mapCenter}
              defaultZoom={13}
              provider={darkTiles}
              dprs={[1, 2]}
              attribution={false}
              metaWheelZoom
              style={{ height: '100%', width: '100%' }}
            >
              {locations.map((loc) => (
                <Marker key={loc.id} width={24} anchor={loc.latLng}>
                  <div className={`pub-marker ${loc.alarm ? 'is-alarm' : loc.online ? 'is-online' : 'is-offline'}`}>
                    <b>{loc.name}</b>
                    <span>{loc.alarm ? 'Alarm' : loc.online ? 'Online' : 'Offline'}</span>
                  </div>
                </Marker>
              ))}
            </Map>
          </div>
        )}
      </section>
    </div>
  );
}
